import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import Header from "../ThemeComponents/Header";
import Footer from "../ThemeComponents/Footer";
import {getAccessoriesOrderAPI} from "../network/publicCalls/POSTAccessoriesCheckout";
import {setAccessoriesMockOrderStatus} from "../mock/accessoriesCheckoutMock";

const ORDER_STATUS_TEXT = {
    draft: "Concept",
    awaiting_registration: "Wacht op registratie",
    awaiting_payment: "Wacht op betaling",
    paid: "Betaald",
    payment_failed: "Betaling mislukt",
    cancelled: "Geannuleerd",
    expired: "Verlopen",
};

const PAYMENT_STATUS_TEXT = {
    pending: "In behandeling",
    paid: "Betaald",
    failed: "Mislukt",
    cancelled: "Geannuleerd",
};

const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "€ 0,00";
    return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

const formatLineLabel = (line) => {
    if (line?.type === "purchase") return `${line?.name || "Product"} (koop)`;
    if (line?.type === "rent") {
        const daysLabel = Number(line?.days) > 0 ? `, ${line.days} dag(en)` : "";
        return `${line?.name || "Product"} (huur${daysLabel})`;
    }
    return line?.name || "Product";
};

const AccessoiresOrderResultPage = () => {
    const {orderId} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const authToken = useMemo(() => {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage.getItem("token") || null;
    }, []);

    const statusFromQuery = String(searchParams.get("payment_status") || "").trim().toLowerCase();
    const shouldApplyMockStatus = Boolean(statusFromQuery);

    const fetchOrder = useCallback(async () => {
        if (!orderId) return;
        setIsLoading(true);
        setErrorMessage("");

        try {
            if (shouldApplyMockStatus) {
                setAccessoriesMockOrderStatus({orderId, paymentStatus: statusFromQuery});
            }
            const response = await getAccessoriesOrderAPI({orderId, authToken});
            setOrder(response);
        } catch (error) {
            setErrorMessage(error?.message || "Bestelling kon niet worden geladen.");
        } finally {
            setIsLoading(false);
        }
    }, [orderId, authToken, shouldApplyMockStatus, statusFromQuery]);

    useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    useEffect(() => {
        if (!order?.status) return;
        if (!["awaiting_payment", "draft"].includes(String(order.status))) return;

        const intervalId = window.setInterval(() => {
            void fetchOrder();
        }, 5000);

        return () => window.clearInterval(intervalId);
    }, [order?.status, fetchOrder]);

    return (
        <>
            <Header/>
            <div style={{height: "70px"}}></div>

            <div className="container mt-5 mb-5">
                <div className="kav-result">
                    <div className="kav-result__icon">
                        <i className="bi bi-receipt"></i>
                    </div>
                    <h1 className="kav-result__title">Accessoires bestelling</h1>
                    <p className="kav-result__subtitle">
                        Bekijk hieronder de actuele status van je bestelling.
                    </p>

                    {isLoading && (
                        <div className="kav-callout kav-auth-status">
                            <span className="kav-spinner" aria-hidden="true"></span>
                            <span>Bestelling laden...</span>
                        </div>
                    )}

                    {!isLoading && errorMessage && (
                        <div className="kav-callout kav-auth-error">
                            {errorMessage}
                        </div>
                    )}

                    {!isLoading && !errorMessage && order && (
                        <>
                            <div className="kav-result__details">
                                <div className="kav-result__row">
                                    <span>Bestelnummer</span>
                                    <strong>{order.order_id || orderId}</strong>
                                </div>
                                <div className="kav-result__row">
                                    <span>Status</span>
                                    <strong>{ORDER_STATUS_TEXT[order.status] || order.status || "-"}</strong>
                                </div>
                                <div className="kav-result__row">
                                    <span>Betaling</span>
                                    <strong>{PAYMENT_STATUS_TEXT[order.payment_status] || order.payment_status || "-"}</strong>
                                </div>
                            </div>

                            <div className="kav-result__details mt-3">
                                {(Array.isArray(order.lines) ? order.lines : []).map((line, index) => (
                                    <div className="kav-result__row" key={`${line?.accessory_id || index}-${index}`}>
                                        <span>{formatLineLabel(line)}</span>
                                        <strong>{formatCurrency(line?.line_total_including_vat)}</strong>
                                    </div>
                                ))}
                                <div className="kav-result__row">
                                    <span>Totaal</span>
                                    <strong>{formatCurrency(order?.totals?.grand_total_including_vat)}</strong>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="kav-result__actions">
                        <button
                            type="button"
                            className="kav-step-actions__btn kav-step-actions__btn--ghost"
                            onClick={() => navigate("/accessoires")}
                        >
                            Terug naar accessoires
                        </button>
                        <Link to="/" className="kav-step-actions__btn kav-step-actions__btn--primary">
                            Naar homepage
                        </Link>
                    </div>
                </div>
            </div>

            <Footer/>
        </>
    );
};

export default AccessoiresOrderResultPage;
